import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import { Howl } from 'howler';
import classNames from 'classnames';
import Icon from '../Icon';
import styles from './Player.module.scss';

const Player = ({ audio = '', card, onPlay, onPause }) => {
    const sound = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [duration, setDuration] = useState(0);
    const [seek, setSeek] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const seekInputRef = useRef(null);

    const loadAndPlay = () => {
        if (audio && sound.current) {
            sound.current.load();
            sound.current.play();
        }
    };

    useEffect(() => {
        if (audio) {
            sound.current = new Howl({
                src: [audio],
                html5: true,
                preload: false,
                onplay: () => {
                    setPlaying(true);
                    onPlay(sound.current);
                },
                onpause: () => {
                    setPlaying(false);
                    onPause();
                },
                onend: () => {
                    setPlaying(false);
                    setSeek(0);
                },
                onload: () => {
                    setDuration(sound.current.duration());
                    setLoaded(true);
                },
            });
        }
    }, [audio]);

    useEffect(() => {
        if (card) return;

        const updateSeekInput = () => {
            if (sound.current && playing) {
                const elapsedTime = sound.current.seek();
                setSeek(elapsedTime);
                if (seekInputRef.current) {
                    seekInputRef.current.value = elapsedTime;
                }
                requestAnimationFrame(updateSeekInput);
            }
        };

        if (playing) {
            requestAnimationFrame(updateSeekInput);
        }
    }, [card, playing]);

    const togglePlayPause = () => {
        if (sound.current) {
            if (playing) {
                sound.current.pause();
                onPause();
            } else {
                loadAndPlay();
                onPlay();
            }
        }
    };

    const handleSeek = (e) => {
        if (sound.current) {
            const newSeek = parseFloat(e.target.value);
            sound.current.seek(newSeek);
            setSeek(newSeek);
        }
    };

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
    };

    return (
        <div
            className={classNames(styles['Player'], {
                [styles['Player--Card']]: card,
            })}>
            {audio && (
                <div className={styles['Player__Controls']}>
                    <button
                        className={styles['Player__Button']}
                        onClick={togglePlayPause}>
                        <Icon type={playing ? 'pause' : 'play'} size="large" />
                    </button>
                    {!card && (
                        <React.Fragment>
                            <div className={styles['Player__Seek']}>
                                <input
                                    ref={seekInputRef}
                                    className={styles['Player__SeekInput']}
                                    type="range"
                                    min={0}
                                    max={duration}
                                    value={seek}
                                    onChange={handleSeek}
                                />
                            </div>
                            {loaded && playing ? (
                                <div className={styles['Player__Time']}>
                                    {formatTime(duration - seek)}
                                </div>
                            ) : (
                                <div
                                    className={styles['Player__Time']}
                                    style={{ visibility: 'hidden' }}>
                                    {formatTime(duration)}
                                </div>
                            )}
                        </React.Fragment>
                    )}
                </div>
            )}
        </div>
    );
};

Player.propTypes = {
    audio: PropTypes.string,
    onPlay: PropTypes.func.isRequired,
    onPause: PropTypes.func.isRequired,
};

export default Player;
